'use client';

import { assureStartsWith } from '@/lib/string';
import { useParams, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

/**
 * Hook to track window.location.hash
 *
 * @param immediateMode If true will check for window hash immediately, use only in cases where returned value won't affect
 * the immediate rendering where it would break ssr/client rendering matching.  The default is to defer everything a tick
 * to make sure it happens past the first render.
 * @returns The current window.location.hash, or empty string if no hash.  Currently this is only helpful client side
 */
export const useLocationHash = (
  immediateMode: boolean = false
): { locationHash: string; setLocationHash: (hash: string) => void } => {
  const [currentLocationHash, setCurrentLocationHash] = useState<string>(
    (immediateMode && typeof window !== 'undefined' && window?.location?.hash) || ''
  );

  const scrapeCurrentLocationHash = useCallback(() => {
    setCurrentLocationHash(window.location.hash);
  }, []);

  const router = useRouter();

  // Returned function to allow simple setting of the location hash
  const setLocationHash = useCallback(
    (hash: string) => {
      if (typeof window !== 'undefined') {
        // make sure the hash starts with a #, as the router.replace() will not add it
        // and we don't want to accidentally navigated
        const normalizedHash = assureStartsWith(hash || '', '#');

        // the next-navigation-guard crashes if we try to set window.location.hash directly on chrome
        // (the hashchange event is not triggered with the correct state data), so we use the
        // router.replace to set the hash (and watchers use useParams() to re-render with the
        // new hash)
        //
        // window.location.hash = normalizedHash;
        router.replace(normalizedHash, { scroll: false });
      }
    },
    [router]
  );

  const params = useParams();
  useEffect(() => {
    scrapeCurrentLocationHash();
  }, [params, scrapeCurrentLocationHash]);

  useEffect(() => {
    // prime the location hash
    scrapeCurrentLocationHash();

    // create a scrapeCurrentLocationHash() proxy for paranoia, as we're adding/removing
    // the hashchange event listener, but it could also be added elsewhere... unlikely,
    // but lets make our registered callback unique
    const onHashChange = () => {
      scrapeCurrentLocationHash();
    };

    // track hash changes
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [scrapeCurrentLocationHash]);

  return { locationHash: currentLocationHash, setLocationHash };
};
